import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Network } from 'lucide-react';

const ASNStatistics = ({ data, isLoading }) => {
  const { t, language } = useLanguage();

  const asnData = data?.asn_stats || [];
  const maxCount = Math.max(...asnData.map(item => item.ip_count), 1);

  const getOrgShortName = (org) => {
    if (!org) return '';
    return org.length > 25 ? org.substring(0, 22) + '...' : org;
  };

  if (isLoading) {
    return (
      <div className="border border-border bg-card h-full">
        <div className="px-5 py-3 border-b border-border bg-muted/30 animate-pulse">
          <div className="h-3 w-40 bg-muted" />
        </div>
        <div className="p-4 space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="animate-pulse h-10 bg-muted/30" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="border border-border bg-card h-full" data-testid="asn-statistics">
      {/* Panel header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <Network className="h-3.5 w-3.5 text-primary" strokeWidth={2.25} />
          <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-foreground">
            {t('asnStatistics')}
          </span>
        </div>
        <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
          TOP 15 · BY IP COUNT
        </span>
      </div>

      {/* Column header */}
      <div className="grid grid-cols-[auto_1fr_auto] gap-2 px-4 py-2 border-b border-border text-[9px] font-mono uppercase tracking-[0.18em] text-muted-foreground bg-muted/10">
        <span className="w-5 text-center">#</span>
        <span>ASN · ORGANIZATION</span>
        <span className="text-right">{language === 'tr' ? 'IP SAYISI' : 'IP COUNT'}</span>
      </div>

      <ScrollArea className="h-[390px]">
        <div>
          {asnData.map((item, index) => (
            <div
              key={item.asn}
              className="group relative px-4 py-2 border-b border-border/60 hover:bg-muted/40 transition-colors"
              data-testid={`asn-item-${index}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className="w-5 text-center text-[10px] font-mono text-muted-foreground tabular">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="soc-badge soc-badge--neutral !py-[2px] !px-1.5 !text-[9px]">
                        AS{item.asn}
                      </span>
                      <span className="text-xs text-foreground truncate" title={item.org}>
                        {getOrgShortName(item.org)}
                      </span>
                    </div>
                  </div>
                </div>
                <span className="text-sm font-mono tabular font-semibold text-foreground">
                  {item.ip_count.toLocaleString()}
                </span>
              </div>
              <div className="mt-1.5 h-[3px] bg-muted overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-500"
                  style={{ width: `${(item.ip_count / maxCount) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

export default ASNStatistics;
